import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { downloadMedia } from '@/lib/contentStudio';
import ZStudioNav from '@/pages/dashboard/apps/zstudio/components/ZStudioNav';
import ZImagePicker from '@/pages/dashboard/apps/zstudio/components/ZImagePicker';
import ZRecentGenerations from '@/pages/dashboard/apps/zstudio/components/ZRecentGenerations';
import {
  createZProject,
  createZCreation,
  logZGeneration,
  editZImage,
  zQualityToApi,
  Z_STYLES,
  Z_ENVIRONMENTS,
  Z_POSES,
  Z_MODEL_TYPES,
  Z_MODEL_APPEARANCES,
  Z_MODEL_HAIR,
  Z_MODEL_SKIN,
  Z_MODEL_BODY,
  Z_MODEL_EXPRESSIONS,
  Z_QUALITIES,
  Z_ASPECTS,
  type ZFactsheet,
} from '@/lib/zstudio';

function Toggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${active ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}>
      {children}
    </button>
  );
}

export default function ZStudioTryOnPage() {
  const { user } = useAuth();
  const [modelPhoto, setModelPhoto] = useState<string | null>(null);
  const [garmentPhoto, setGarmentPhoto] = useState<string | null>(null);
  const [garmentDesc, setGarmentDesc] = useState('');
  const [reference, setReference] = useState('');
  const [modelType, setModelType] = useState(Z_MODEL_TYPES[0]);
  const [appearance, setAppearance] = useState(Z_MODEL_APPEARANCES[0]);
  const [hair, setHair] = useState(Z_MODEL_HAIR[0]);
  const [skin, setSkin] = useState(Z_MODEL_SKIN[0]);
  const [body, setBody] = useState(Z_MODEL_BODY[0]);
  const [expression, setExpression] = useState(Z_MODEL_EXPRESSIONS[0]);
  const [style, setStyle] = useState(Z_STYLES[0]);
  const [environment, setEnvironment] = useState(Z_ENVIRONMENTS[0]);
  const [pose, setPose] = useState(Z_POSES[0]);
  const [quality, setQuality] = useState('standard');
  const [aspect, setAspect] = useState('3:4');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const canRun = !running && (!!modelPhoto || modelingByDescription()) && (!!garmentPhoto || garmentDesc.trim().length > 0);

  function modelingByDescription() {
    return modelType.trim() !== '';
  }

  const buildPrompt = (): string => {
    const garmentDescText = garmentDesc.trim() || 'the garment shown in the reference image';
    const parts: string[] = [];
    if (modelPhoto && garmentPhoto) {
      parts.push('Using <IMAGE_0> as the person and <IMAGE_1> as the garment reference.');
      parts.push('Dress EXACTLY the same person from <IMAGE_0> in the garment from <IMAGE_1>.');
    } else if (modelPhoto) {
      parts.push('Using <IMAGE_0> as the person reference.');
      parts.push(`Dress this exact person in ${garmentDescText}.`);
    } else if (garmentPhoto) {
      parts.push('Using <IMAGE_0> as the garment reference.');
      parts.push(`Create a realistic ${modelType.toLowerCase()} model with ${appearance.toLowerCase()} look, ${hair.toLowerCase()} hair, ${skin.toLowerCase()} skin, ${body.toLowerCase()} build, ${expression.toLowerCase()} expression, wearing EXACTLY the garment from <IMAGE_0>.`);
    } else {
      parts.push(`Create a realistic fashion photo of a ${modelType.toLowerCase()} model with ${appearance.toLowerCase()} look, ${hair.toLowerCase()} hair, ${skin.toLowerCase()} skin, ${body.toLowerCase()} build, ${expression.toLowerCase()} expression, wearing ${garmentDescText}.`);
    }
    parts.push('CRITICAL: preserve the garment identity exactly — colour, cut, silhouette, pattern, texture, embroidery, buttons, seams, sleeves and length must stay identical. Do not redesign or change the garment.');
    if (reference.trim()) parts.push(`Additional context: ${reference.trim()}.`);
    parts.push(`Style: ${style}. Environment: ${environment}. Pose: ${pose}.`);
    parts.push('Professional fashion photography, natural skin and fabric textures, soft studio lighting, sharp details, editorial quality, no text, no watermark.');
    return parts.join(' ');
  };

  const run = async () => {
    if (!user?.id || !canRun) return;
    setRunning(true);
    setError(null);
    setResult(null);
    setProgress('Analyse des images…');
    try {
      const q = zQualityToApi(quality);
      const sources = [modelPhoto, garmentPhoto].filter(Boolean) as string[];
      const subjectName = garmentDesc.trim() || 'Essayage virtuel';
      const project = await createZProject({
        ownerId: user.id,
        name: subjectName.slice(0, 120),
        kind: 'fashion',
        aspectRatio: aspect,
        sourceType: 'tryon',
        brief: garmentDesc || undefined,
        meta: { modelType, style, environment, pose },
      });

      setProgress('Génération de l’essayage (IA)…');
      const urls = await editZImage({
        prompt: buildPrompt(),
        images: sources,
        aspectRatio: aspect,
        quality: q.quality,
        resolution: q.resolution,
        persist: true,
        folder: 'zstudio/tryon',
      });
      const url = urls[0] || null;
      if (!url) throw new Error('Aucune image générée.');
      setResult(url);

      const factsheet: ZFactsheet = { name: subjectName, kind: 'Essayage virtuel', description: garmentDesc || undefined };
      await createZCreation({
        ownerId: user.id,
        projectId: project.id,
        kind: 'tryon',
        mediaKind: 'image',
        url,
        thumbnail_url: url,
        title: `Try-on — ${subjectName}`,
        content: { prompt: buildPrompt(), sources, aspect, quality, factsheet },
        provider: 'xAI',
      });
      await logZGeneration({
        ownerId: user.id,
        projectId: project.id,
        type: 'tryon',
        prompt: buildPrompt(),
        model: 'grok-imagine-image-2.0',
        provider: 'xAI',
        output: { url },
        status: 'done',
      });
      setProgress('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La génération a échoué.');
      setProgress('');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="mb-4">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-user-star-line text-accent-500"></i>Essayage virtuel
        </h2>
        <p className="text-sm text-foreground-500 mt-1">Photo du modèle + photo du vêtement → une mise en scène réaliste. Le vêtement réel est conservé au maximum (spec 41 & 65).</p>
      </div>
      <ZStudioNav />
      <ZRecentGenerations />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Colonne gauche — entrées */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ZImagePicker value={modelPhoto} onChange={setModelPhoto} label="Photo du modèle" hint="Le mannequin / la personne (optionnel si vous décrivez)." />
            <ZImagePicker value={garmentPhoto} onChange={setGarmentPhoto} label="Photo du vêtement" hint="Le vêtement réel à conserver (optionnel)." />
          </div>

          <div>
            <p className="text-xs font-medium text-foreground-600 mb-1.5">Décrire le vêtement (optionnel)</p>
            <textarea value={garmentDesc} onChange={(e) => setGarmentDesc(e.target.value)} rows={2} placeholder="Ex : longue robe fluide, broderie dorée, couleur ivoire, manches longues…" className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-300 resize-none" />
          </div>

          <div>
            <p className="text-xs font-medium text-foreground-600 mb-1.5">Contexte additionnel (optionnel)</p>
            <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Ex : conserve exactement la broderie" className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-300" />
          </div>

          <div className="border-t border-background-200/70 pt-4">
            <p className="text-xs font-bold text-foreground-700 mb-2 flex items-center gap-1.5"><i className="ri-user-settings-line text-primary-500"></i>Personnalisation du mannequin</p>
            <div className="space-y-3">
              <div>
                <p className="text-[11px] text-foreground-500 mb-1">Type de modèle</p>
                <div className="flex flex-wrap gap-1.5">{Z_MODEL_TYPES.map((m) => <Toggle key={m} active={modelType === m} onClick={() => setModelType(m)}>{m}</Toggle>)}</div>
              </div>
              <div>
                <p className="text-[11px] text-foreground-500 mb-1">Apparence</p>
                <div className="flex flex-wrap gap-1.5">{Z_MODEL_APPEARANCES.map((m) => <Toggle key={m} active={appearance === m} onClick={() => setAppearance(m)}>{m}</Toggle>)}</div>
              </div>
              <div>
                <p className="text-[11px] text-foreground-500 mb-1">Cheveux</p>
                <div className="flex flex-wrap gap-1.5">{Z_MODEL_HAIR.map((m) => <Toggle key={m} active={hair === m} onClick={() => setHair(m)}>{m}</Toggle>)}</div>
              </div>
              <div>
                <p className="text-[11px] text-foreground-500 mb-1">Carnation</p>
                <div className="flex flex-wrap gap-1.5">{Z_MODEL_SKIN.map((m) => <Toggle key={m} active={skin === m} onClick={() => setSkin(m)}>{m}</Toggle>)}</div>
              </div>
              <div>
                <p className="text-[11px] text-foreground-500 mb-1">Silhouette</p>
                <div className="flex flex-wrap gap-1.5">{Z_MODEL_BODY.map((m) => <Toggle key={m} active={body === m} onClick={() => setBody(m)}>{m}</Toggle>)}</div>
              </div>
              <div>
                <p className="text-[11px] text-foreground-500 mb-1">Expression</p>
                <div className="flex flex-wrap gap-1.5">{Z_MODEL_EXPRESSIONS.map((m) => <Toggle key={m} active={expression === m} onClick={() => setExpression(m)}>{m}</Toggle>)}</div>
              </div>
            </div>
          </div>

          <div className="border-t border-background-200/70 pt-4 space-y-3">
            <div>
              <p className="text-xs font-medium text-foreground-600 mb-1.5">Style</p>
              <div className="flex flex-wrap gap-1.5">{Z_STYLES.map((s) => <Toggle key={s} active={style === s} onClick={() => setStyle(s)}>{s}</Toggle>)}</div>
            </div>
            <div>
              <p className="text-xs font-medium text-foreground-600 mb-1.5">Environnement</p>
              <div className="flex flex-wrap gap-1.5">{Z_ENVIRONMENTS.map((s) => <Toggle key={s} active={environment === s} onClick={() => setEnvironment(s)}>{s}</Toggle>)}</div>
            </div>
            <div>
              <p className="text-xs font-medium text-foreground-600 mb-1.5">Pose</p>
              <div className="flex flex-wrap gap-1.5">{Z_POSES.map((s) => <Toggle key={s} active={pose === s} onClick={() => setPose(s)}>{s}</Toggle>)}</div>
            </div>
            <div>
              <p className="text-xs font-medium text-foreground-600 mb-1.5">Format</p>
              <div className="flex flex-wrap gap-1.5">
                {[...Z_ASPECTS, { value: '3:4', label: 'Portrait 3:4', hint: 'Mode' }].map((a) => <Toggle key={a.value} active={aspect === a.value} onClick={() => setAspect(a.value)}>{a.label}</Toggle>)}
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-foreground-600 mb-1.5">Qualité</p>
              <div className="flex flex-wrap gap-1.5">{Z_QUALITIES.map((q) => <Toggle key={q.value} active={quality === q.value} onClick={() => setQuality(q.value)}>{q.label}</Toggle>)}</div>
            </div>
          </div>
        </div>

        {/* Colonne droite — résultat */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 flex flex-col">
          <div className="flex-1 flex items-center justify-center min-h-[320px] bg-background-100 rounded-lg overflow-hidden">
            {running ? (
              <div className="text-center px-4">
                <i className="ri-loader-4-line animate-spin text-3xl text-accent-500"></i>
                <p className="text-sm text-foreground-600 mt-3">{progress || 'Génération…'}</p>
              </div>
            ) : result ? (
              <img src={result} alt="Essayage" className="w-full h-full object-contain" />
            ) : (
              <div className="text-center px-6">
                <span className="w-14 h-14 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-shirt-line text-2xl text-foreground-400"></i>
                </span>
                <p className="text-sm text-foreground-500">L’aperçu de l’essayage apparaîtra ici.</p>
              </div>
            )}
          </div>

          {error && <div className="mt-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg"><i className="ri-error-warning-line"></i>{error}</div>}

          {result && !running && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button type="button" onClick={() => downloadMedia(result, 'zstudio-tryon.png')} className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-download-2-line"></i>Télécharger
              </button>
              <Link to="/dashboard/zstudio/creations" className="flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-archive-2-line"></i>Mes créations
              </Link>
              <button type="button" onClick={run} className="flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-refresh-line"></i>Régénérer
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={run}
            disabled={!canRun || running}
            className="mt-4 flex items-center justify-center gap-1.5 px-5 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            {running ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-magic-line"></i>}
            {running ? 'Génération…' : 'Générer l’essayage'}
          </button>
          <p className="text-[11px] text-foreground-400 mt-2">Fournissez au moins un repère (modèle OU vêtement) pour un résultat fidèle. La cohérence du mannequin est conservée d’une image à l’autre si vous réutilisez la même photo de modèle (spec 10).</p>
        </div>
      </div>
    </div>
  );
}